import { createRouter, createWebHistory } from 'vue-router';
import { msalInstance, initializeMsal } from '@/auth/msalInstance';
import { SET_USER_GROUPS } from '@/store/mutation-types';
import { ENV, GROUP_IDS_BY_ENV, useAuthStore } from '@/store/modules/auth';

import HomePage from '@/views/home-page.vue';
import MarinLitList from '@/views/marinlit/article-list.vue';
import MarinLitEdit from '@/views/marinlit/article-edit.vue';
import MerckIndexList from '@/views/merckindex/monograph-list.vue';
import MerckIndexEdit from '@/views/merckindex/monograph-edit.vue';
import CSDepositions from '@/views/cs-depositions/cs-depositions-list.vue';
import CSDepositionsEdit from '@/views/cs-depositions/cs-depositions-edit.vue';
import CSDepositionsAdd from '@/views/cs-depositions/cs-depositions-add.vue';
import CSDatasources from '@/views/cs-depositions/cs-datasources-list.vue';
import CSDatasourcesView from '@/views/cs-depositions/cs-datasources-view.vue';
import CSDatasourcesEdit from '@/views/cs-depositions/cs-datasources-edit.vue';
import CSDatasourcesAdd from '@/views/cs-depositions/cs-datasources-add.vue';
import CurationDataSources from '@/views/curation/curation-datasources.vue';
import CurationDeprecations from '@/views/curation/curation-deprecations.vue';
import CurationSynonyms from '@/views/curation/curation-synonyms.vue';
import PageNotFound from '@/views/page-not-found.vue';
import LoginCallback from '@/components/auth/login-callback.vue';

// Routes
const routes = [
  { 
    path: '/',
    name: 'Home',
    component: HomePage,
    meta: { elementId: 'page-home' },
  },
  {
    path: '/marinlit',
    name: 'MarinLit',
    component: MarinLitList,
    meta: { elementId: 'page-marinlit-list', requiresAuth: true, requiredGroup: 'MARINLIT', },
  },
  {
    path: '/marinlit/edit/:id?',
    name: 'MarinLitEdit',
    component: MarinLitEdit,
    meta: { elementId: 'page-marinlit-edit', requiresAuth: true, requiredGroup: 'MARINLIT', },
  },
  {
    path: '/merckindex',
    name: 'MerckIndex',
    component: MerckIndexList,
    meta: { elementId: 'page-merckindex-list', requiresAuth: true, requiredGroup: 'MERCKINDEX', },
  },
  {
    path: '/merckindex/edit/:id',
    name: 'MerckIndexEdit',
    component: MerckIndexEdit,
    meta: { elementId: 'page-merckindex-edit', requiresAuth: true, requiredGroup: 'MERCKINDEX', },
  },
  {
    path: '/cs-depositions',
    name: 'CSDepositions',
    component: CSDepositions,
    meta: { elementId: 'page-cs-depositions', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/cs-depositions/edit/:id',
    name: 'CSDepositionsEdit',
    component: CSDepositionsEdit,
    meta: { elementId: 'page-cs-depositions-edit', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/cs-depositions/add',
    name: 'CSDepositionsAdd',
    component: CSDepositionsAdd,
    meta: { elementId: 'page-cs-depositions-add', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/cs-depositions/datasources',
    name: 'CSDatasources',
    component: CSDatasources,
    meta: { elementId: 'page-cs-datasources', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/cs-depositions/datasource/add',
    name: 'CSDatasourcesAdd',
    component: CSDatasourcesAdd,
    meta: { elementId: 'page-cs-datasources-add', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/cs-depositions/datasource/edit/:id',
    name: 'CSDatasourcesEdit',
    component: CSDatasourcesEdit,
    meta: { elementId: 'page-cs-datasources-edit', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/cs-depositions/datasource/:id',
    name: 'CSDatasourcesView',
    component: CSDatasourcesView,
    meta: { elementId: 'page-cs-datasources-view', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/curation/data-sources',
    name: 'CurationDataSources',
    component: CurationDataSources,
    meta: { elementId: 'page-curation-datasources', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/curation/deprecations',
    name: 'CurationDeprecations',
    component: CurationDeprecations,
    meta: { elementId: 'page-curation-deprecations', requiresAuth: true, requiredGroup: 'CHEMSPIDER',},
  },
  {
    path: '/curation/synonyms',
    name: 'CurationSynonyms',
    component: CurationSynonyms,
    meta: { elementId: 'page-curation-synonyms', requiresAuth: true, requiredGroup: 'CHEMSPIDER', },
  },
  {
    path: '/login/callback',
    name: 'LoginCallback',
    component: LoginCallback,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: PageNotFound,
    meta: { elementId: 'page-not-found' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  
  linkActiveClass: 'is-active',
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { x: 0, y: 0 };
  },
});

// MSAL & Auth guard
let msalInitialized = false;
router.beforeEach(async (to, from, next) => {
  if (!msalInitialized) {
    try {
      await initializeMsal();
      msalInitialized = true;
    } catch (error) {
      console.error('Error starting MSAL:', error);
      return next(false);
    }
  }

  const authStore = useAuthStore();

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiredGroupKey = to.meta.requiredGroup;

  // --- START: local bypass. Clickable tabs ---
  if (!import.meta.env.PROD) {
    // in local, simulates an user with all its groups
    if (!authStore.currentGroups.length) {
      authStore[SET_USER_GROUPS](Object.values(GROUP_IDS_BY_ENV.dev));
    }
    return next();
  }
  // --- END: local bypass ---


  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

  if (requiresAuth && !account) {
    // No logged in account... redirecting to login page
    return msalInstance.loginRedirect({ scopes: ['openid', 'profile', 'email'] });
  }

  if (account) {
    msalInstance.setActiveAccount(account);
    if (!authStore.currentGroups.length) {
      await authStore.updateAuth();
    }

    if (requiredGroupKey) {
      const userGroups = authStore.userGroups;
      const requiredGroupId = GROUP_IDS_BY_ENV?.[ENV]?.[requiredGroupKey];
      if (!requiredGroupId || !userGroups.includes(requiredGroupId)) {
        return next('/');
      }
    }
  }
  next();
});

router.afterEach((to) => {
  const routeTitle = to.matched.slice().reverse().find(r => r.meta?.title)?.meta.title
  const title = routeTitle ? `${routeTitle} - Databases Workbench` : 'Databases Workbench'
  document.title = title
})


export default router;